import {
  FETCH_TASKS,
  FETCH_TASKS_SUCCESS,
  FETCH_TASKS_FAILED,
  ACCEPT_TASK,
  CANCEL_TASK,
} from "../store/types";
import store from "../store/store";
import { updateProfile } from "./authactions";
import { RequestPushMsg } from "../other/NotificationFunctions";
import { firebase } from '../config/configureFirebase';
import { onValue, runTransaction, get, push, update } from "firebase/database";
import {driverQueue} from '../other/sharedFunctions';
import { resolveRemoteImageUri } from '../other/remoteImageUri';

let taskListeners = null;

const stopTaskListeners = () => {
  if (!taskListeners) return;
  if (taskListeners.inbox) taskListeners.inbox();
  Object.values(taskListeners.bookings).forEach((unsubscribe) => unsubscribe());
  taskListeners = null;
};

/**
 * Solicitudes del conductor: escucha su bandeja driver_requests/{uid}, que mantiene el servidor,
 * y cada reserva que aparece en ella. No descarga las reservas NEW de los demás conductores.
 */
export const fetchTasks = () => (dispatch) => {
  const { auth, driverRequestsRef, singleBookingRef } = firebase;

  const uid = auth.currentUser.uid;
  stopTaskListeners();
  const listeners = { inbox: null, bookings: {} };
  taskListeners = listeners;
  const bookings = {};
  const pending = new Set();
  let inboxIds = null;

  dispatch({
    type: FETCH_TASKS,
    payload: null,
  });

  const emit = () => {
    if (taskListeners !== listeners || inboxIds === null || pending.size) return;
    if (!inboxIds.length) {
      dispatch({
        type: FETCH_TASKS_FAILED,
        payload: store.getState().languagedata.defaultLanguage.no_tasks,
      });
      return;
    }
    const arr = inboxIds
      .filter((id) => {
        const booking = bookings[id];
        return booking && booking.status === 'NEW' && booking.requestedDrivers && booking.requestedDrivers[uid];
      })
      .map((id) => ({ ...bookings[id], id }));
    dispatch({
      type: FETCH_TASKS_SUCCESS,
      payload: arr,
    });
  };

  const dropBooking = (id) => {
    if (listeners.bookings[id]) listeners.bookings[id]();
    delete listeners.bookings[id];
    delete bookings[id];
    pending.delete(id);
  };

  listeners.inbox = onValue(driverRequestsRef(uid), (snapshot) => {
    if (taskListeners !== listeners) return;
    inboxIds = Object.keys(snapshot.val() || {}).sort();
    Object.keys(listeners.bookings).forEach((id) => {
      if (inboxIds.indexOf(id) === -1) dropBooking(id);
    });
    inboxIds.forEach((id) => {
      if (listeners.bookings[id]) return;
      pending.add(id);
      listeners.bookings[id] = onValue(singleBookingRef(id), (bookingSnap) => {
        if (taskListeners !== listeners) return;
        bookings[id] = bookingSnap.val();
        pending.delete(id);
        emit();
      }, () => {
        // Sin permiso (otro conductor aceptó y las reglas ya no lo dejan leer) o reserva borrada.
        bookings[id] = null;
        pending.delete(id);
        emit();
      });
    });
    emit();
  }, () => {
    if (taskListeners !== listeners) return;
    inboxIds = [];
    emit();
  });
};

export const acceptTask = (task) => (dispatch) => {
  const { auth, trackingRef, singleUserRef, singleBookingRef, get } = firebase;

  const uid = auth.currentUser.uid;

  return new Promise((resolve) => {
    onValue(singleUserRef(uid), async (snapshot) => {
      try {
        let profile = snapshot.val();

        let driverToken = profile?.pushToken;
        
        if (!driverToken || 
            driverToken === '' || 
            driverToken === 'token_error' || 
            driverToken === 'SERVICE_NOT_AVAILABLE' ||
            driverToken === 'null') {
          
          console.error('[acceptTask] Conductor sin pushToken válido:', driverToken);
          try {
            const userSnapshot = await get(singleUserRef(uid));
            if (userSnapshot.exists()) {
              const userData = userSnapshot.val();
              if (userData.pushToken && 
                  userData.pushToken !== 'token_error' && 
                  userData.pushToken !== 'SERVICE_NOT_AVAILABLE' &&
                  userData.pushToken !== 'null') {
                driverToken = userData.pushToken;
                profile.pushToken = userData.pushToken;
              }
            }
          } catch (error) {
            console.error('[acceptTask] Error obteniendo pushToken:', error);
          }
        }

        let assigned = false;
        const txResult = await runTransaction(singleBookingRef(task.id),(booking) => {
          assigned = false;
          if (booking === null) return null;
          const assignable = booking.status === 'NEW' || booking.status === 'PAYMENT_PENDING';
          if (!assignable || !booking.requestedDrivers || !booking.requestedDrivers[uid]) {
            return;
          }
          if (booking.driver && booking.driver !== uid) {
            return;
          }
          let fleetCommission_fee= profile?.fleetadmin ? ((parseFloat(booking?.estimate) - parseFloat(booking?.convenience_fees)) * parseFloat(booking?.fleet_admin_comission) / 100).toFixed(2):0;
          let driver_fee = parseFloat(parseFloat(booking?.estimate) - (parseFloat(booking?.convenience_fees) + parseFloat(fleetCommission_fee) )).toFixed(2);
              booking.driver = uid;
              booking.driver_image = resolveRemoteImageUri(profile.profile_image) || '';
              booking.car_image = resolveRemoteImageUri(profile.car_image) || '';
              booking.driver_name = profile.firstName + " " + profile.lastName;
              booking.driver_contact = profile.mobile;
              booking.driver_token = driverToken || '';
              booking.vehicle_number = profile.vehicleNumber ? profile.vehicleNumber : "";
              booking.vehicleModel = profile.vehicleModel ? profile.vehicleModel : "";
              booking.vehicleMake = profile.vehicleMake ? profile.vehicleMake : "";
              // Color / notas del vehículo (other_info en el auto activo del conductor).
              booking.other_info = profile.other_info ? profile.other_info : (booking.other_info || "");
              booking.vehicleColor = profile.vehicleColor
                ? profile.vehicleColor
                : (profile.other_info || "");
              booking.driverRating = profile.rating ? profile.rating : "0";
              booking.fleetCommission= fleetCommission_fee? fleetCommission_fee : "0"
              booking.fleetadmin = profile.fleetadmin ? profile.fleetadmin : "";
              booking.status = "ACCEPTED";
              booking.driver_share = driver_fee? driver_fee : "0";
              booking.driverDeviceId = task.driverDeviceId? task.driverDeviceId: null;
              booking.requestedDrivers = null;
              booking.driverEstimates = null;
              assigned = true;
              return booking;
          });

        if (!txResult || !txResult.committed || !assigned) {
          console.warn('[acceptTask] Aceptación rechazada (carrera o status inválido):', task.id);
          resolve({ ok: false, reason: 'unavailable' });
          return;
        }

        const snap = await get(singleBookingRef(task.id));
        if (!snap.exists() || snap.val().driver !== uid) {
          resolve({ ok: false, reason: 'unavailable' });
          return;
        }

        updateProfile({ queue: driverQueue ? false : true })(dispatch);
        if (task.customer_token || task.customer) {
          RequestPushMsg(task.customer_token, {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg:
              profile.firstName +
              store.getState().languagedata.defaultLanguage.accept_booking_request,
            screen: "BookedCab",
            params: { bookingId: task.id },
          }, task.customer);
        }

        const driverLocation = store.getState().gpsdata.location;
        if (driverLocation?.lat != null && driverLocation?.lng != null) {
          push(trackingRef(task.id), {
            at: new Date().getTime(),
            status: "ACCEPTED",
            lat: driverLocation.lat,
            lng: driverLocation.lng,
          });
        }

        dispatch({
          type: ACCEPT_TASK,
          payload: { task: task },
        });
        resolve({ ok: true });
      } catch (error) {
        console.error('[acceptTask] error', error);
        resolve({ ok: false, reason: 'error' });
      }
    },{onlyOnce:true});
  });
};

export const cancelTask = (bookingId) => async (dispatch) => {
  const { auth, singleBookingRef } = firebase;
  const uid = auth.currentUser.uid;
  if (!bookingId || !uid) return;

  let released = false;
  const result = await runTransaction(singleBookingRef(bookingId), (booking) => {
    released = false;
    if (booking === null) return null;
    if (booking.driver) return;
    if (booking.status !== 'NEW' && booking.status !== 'PAYMENT_PENDING') return;

    const hasRequest = !!(booking.requestedDrivers && booking.requestedDrivers[uid]);
    const hasOffer = !!(booking.driverOffers && booking.driverOffers[uid]);
    // Tras ofertar, el conductor puede ignorar: hay que quitar request y/o oferta.
    if (!hasRequest && !hasOffer) return;

    const next = { ...booking };

    if (booking.requestedDrivers) {
      const requested = { ...booking.requestedDrivers };
      delete requested[uid];
      next.requestedDrivers = Object.keys(requested).length ? requested : null;
      if (!next.requestedDrivers) {
        next.driverEstimates = null;
      }
    }

    // null explícito: RTDB borra el nodo; {} / omitir deja la oferta visible al pasajero (merge Redux).
    if (booking.driverOffers) {
      const offers = { ...booking.driverOffers };
      delete offers[uid];
      next.driverOffers = Object.keys(offers).length ? offers : null;
    }

    if (booking.driverEstimates && booking.driverEstimates[uid]) {
      const estimates = { ...booking.driverEstimates };
      delete estimates[uid];
      next.driverEstimates = Object.keys(estimates).length ? estimates : null;
    }

    if (next.selectedBid && next.selectedBid.driver === uid) {
      next.selectedBid = null;
    }

    released = true;
    return next;
  });

  if (!result || !result.committed || !released) {
    return;
  }

  // Refuerzo: borrar path de oferta (por si el merge del cliente o la TX no limpió el hijo).
  try {
    await update(singleBookingRef(bookingId), {
      ['driverOffers/' + uid]: null,
      ['requestedDrivers/' + uid]: null,
      ['driverEstimates/' + uid]: null,
    });
  } catch (_) {
    /* noop — la TX ya aplicó lo esencial */
  }

  dispatch({
    type: CANCEL_TASK,
    payload: { uid: uid, bookingId: bookingId },
  });
  // No enviar "booking_cancelled": el viaje sigue en NEW; solo se ignoró la solicitud.
  // Avisar cancelación real es responsabilidad de cancelBooking (status CANCELLED).
};
